<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true" />
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      @if (detail(); as d) {
        <p-tag [severity]="displayStatusSeverity(d.displayStatus)" [value]="displayStatusLabel(d.displayStatus)" />
      }
    </div>
    <div class="buttons">
      <p-button variant="outlined" severity="secondary" icon="pi pi-arrow-left" (click)="back()" [label]="'Back' | translateWF: 'Back'" />
    </div>
  </header>

  <div class="content">
    @if (detail(); as d) {
      <p class="po-detail__subtitle">
        {{ d.providerName }} · {{ 'PoSettlement_Ordered' | translateWF: 'Ordered' }} {{ d.issuedDate | date: 'dd/MM/yyyy' }} · {{ d.documents.length }}
        {{ 'PoSettlement_Documents' | translateWF: 'document(s)' }}
      </p>

      <p-panel header="Summary">
        <div class="col-xs">
          <div class="cs-summary-grid">
            <p-card data-variant="summary" [header]="'PoSettlement_PoTotal' | translateWF: 'PO Total'">
              <ng-template pTemplate="subtitle">{{ d.invoiced | number: '1.2-2' }} {{ currencyLabel(d.currency) }}</ng-template>
            </p-card>
            <p-card data-variant="summary" [header]="'PoSettlement_Requested' | translateWF: 'Requested'">
              <ng-template pTemplate="subtitle">{{ d.requested | number: '1.2-2' }} {{ currencyLabel(d.currency) }}</ng-template>
            </p-card>
            <p-card data-variant="summary" data-severity="success" [header]="'PoSettlement_Paid' | translateWF: 'Paid'">
              <ng-template pTemplate="subtitle">{{ d.paid | number: '1.2-2' }} {{ currencyLabel(d.currency) }}</ng-template>
            </p-card>
            <!-- #4346–#4350 — only when there is an advance, so a PO without one keeps the original four cards.
                 Applied is the headline because it is what settles AP and what Balance subtracts; allocated is
                 shown beneath it, since cash that has left the bank but not been drawn down is a real fact the
                 clerk needs but not the one the arithmetic uses. -->
            @if (d.advanceAllocated > 0 || d.advanceApplied > 0) {
              <p-card data-variant="summary" data-severity="success" [header]="'PoSettlement_AdvanceApplied' | translateWF: 'Advance applied'">
                <ng-template pTemplate="subtitle">{{ d.advanceApplied | number: '1.2-2' }} {{ currencyLabel(d.currency) }}</ng-template>
                <div>
                  {{ d.advanceAllocated | number: '1.2-2' }}
                  {{
                    'PoSettlement_AdvanceAllocated'
                      | translateWF
                        : 'advanced in
              total'
                  }}
                </div>
              </p-card>
            }
            <!-- #4454 — only when something was withheld, on the same rule as the advance card above. Withholding
                 discharges the payable without the provider ever being paid it, so it reduces Balance exactly as
                 a payment does; leaving it uncounted held the PO short by the retention for good. -->
            @if (d.withheld > 0) {
              <p-card data-variant="summary" data-severity="success" [header]="'PoSettlement_Withheld' | translateWF: 'Withheld'">
                <ng-template pTemplate="subtitle">{{ d.withheld | number: '1.2-2' }} {{ currencyLabel(d.currency) }}</ng-template>
                <div>{{ 'PoSettlement_WithheldHint' | translateWF: 'retained for SAR, not payable' }}</div>
              </p-card>
            }
            <p-card data-variant="summary" [header]="'PoSettlement_Balance' | translateWF: 'Balance'">
              <ng-template pTemplate="subtitle">{{ d.remaining | number: '1.2-2' }} {{ currencyLabel(d.currency) }}</ng-template>
            </p-card>
          </div>
          <p-card>
            <div class="row">
              <p>{{ paidPercent() }}% {{ 'PoSettlement_PaidLower' | translateWF: 'paid' }}</p>
              <p-progressBar [value]="paidPercent()" [showValue]="false" styleClass="po-detail__bar cs-progressbar-lg" />
            </div>
          </p-card>
        </div>
      </p-panel>

      <div class="grid-12">
        <!-- AP documents -->
        <div class="span-12-8-8">
          <p-panel [header]="'PoSettlement_ApDocuments' | translateWF: 'AP documents'">
            <p-card>
              @if (!d.documents.length) {
                <div class="po-detail__empty">{{ 'PoSettlement_NoDocs' | translateWF: 'No AP documents yet' }}</div>
              }

              @for (doc of d.documents; track doc.id) {
                <div class="doc-card">
                  <div class="doc-card__head">
                    <div class="doc-card__identity">
                      <!-- #4893 — the shared identity, so the fiscal number leads here exactly as it does on the
                       AP documents list. It replaces the "Fiscal inv." entry that used to sit in the meta
                       row below, where it read as one more attribute rather than as the document's name. -->
                      <app-document-identity [documentNumber]="doc.number" [fiscalNumber]="doc.fiscalNumber" [clickable]="false" />
                      <p-tag severity="info" [value]="docStatusLabel(doc.status)" />
                    </div>
                    <span class="doc-card__total tabular-nums">{{ doc.total | number: '1.2-2' }}</span>
                  </div>
                  <div class="doc-card__meta">
                    <span>{{ doc.documentDate | date: 'dd/MM/yyyy' }}</span>
                    <span>{{ 'PoSettlement_Due' | translateWF: 'due' }} {{ doc.dueDate | date: 'dd/MM/yyyy' }}</span>
                  </div>

                  <div class="doc-card__amounts">
                    <div>
                      <span class="doc-card__amount-label">{{ 'PoSettlement_Total' | translateWF: 'Total' }}</span>
                      <span class="tabular-nums">{{ doc.total | number: '1.2-2' }}</span>
                    </div>
                    <div>
                      <span class="doc-card__amount-label">{{ 'PoSettlement_Paid' | translateWF: 'Paid' }}</span>
                      <span class="tabular-nums doc-card__paid">{{ doc.paid | number: '1.2-2' }}</span>
                    </div>
                    <!-- Shown only when there is one, but then it is not optional: Balance is the document's own
                     BalanceDue, so without this the three figures on the card would not add up. -->
                    @if (doc.withheld > 0) {
                      <div>
                        <span class="doc-card__amount-label">{{ 'PoSettlement_Withheld' | translateWF: 'Withheld' }}</span>
                        <span class="tabular-nums doc-card__paid">{{ doc.withheld | number: '1.2-2' }}</span>
                      </div>
                    }
                    <div>
                      <span class="doc-card__amount-label">{{ 'PoSettlement_Balance' | translateWF: 'Balance' }}</span>
                      <span class="tabular-nums">{{ doc.balance | number: '1.2-2' }}</span>
                    </div>
                  </div>

                  @if (doc.requests.length) {
                    <div class="doc-card__sub-title">{{ 'PoSettlement_PaymentRequests' | translateWF: 'Payment requests' }}</div>
                    @for (req of doc.requests; track req.id) {
                      <div class="doc-card__line">
                        <span class="doc-card__line-icon"><i class="pi pi-send"></i></span>
                        <span class="doc-card__line-main">
                          {{ req.number }}
                          <span class="doc-card__line-sub">{{ req.date | date: 'dd/MM/yyyy' }} · {{ req.actor }}</span>
                        </span>
                        <p-tag severity="secondary" [value]="requestStatusLabel(req.status)" />
                        <span class="tabular-nums doc-card__line-amount">{{ req.amount | number: '1.2-2' }}</span>
                      </div>
                    }
                  }

                  @if (doc.payments.length) {
                    <div class="doc-card__sub-title">{{ 'PoSettlement_Payments' | translateWF: 'Payments' }}</div>
                    @for (pay of doc.payments; track pay.id) {
                      <div class="doc-card__line">
                        <span class="doc-card__line-icon doc-card__line-icon--paid"><i class="pi pi-check-circle"></i></span>
                        <span class="doc-card__line-main">
                          {{ pay.number }}
                          <span class="doc-card__line-sub">
                            {{ pay.date | date: 'dd/MM/yyyy' }} · {{ currencyLabel(pay.currency) }}
                            @if (pay.bankAccountMasked) {
                              · {{ pay.bankAccountMasked }}
                            }
                          </span>
                        </span>
                        <span class="tabular-nums doc-card__line-amount">{{ pay.amount | number: '1.2-2' }}</span>
                      </div>
                    }
                  }
                </div>
              }
            </p-card>
          </p-panel>
        </div>

        <!-- Activity timeline + advances -->
        <div class="span-12-4-4">
          <!-- Vendor advances (#4346–#4350). Listed individually rather than only as a total: an advance is
               cash already paid to the provider, and the clerk needs to see which ones still have room to be
               drawn down before deciding what to request. -->
          @if (d.advances.length) {
            <p-panel class="mb-[1.5rem]" [header]="'PoSettlement_Advances' | translateWF: 'Vendor advances'">
              <p-card>
                @for (adv of d.advances; track adv.id) {
                  <div class="doc-card mb-[0.75rem]">
                    <div class="doc-card__head">
                      <div class="doc-card__identity">
                        <span>{{ adv.number }}</span>
                        @if (adv.remaining > 0) {
                          <p-tag severity="info" [value]="'PoSettlement_AdvanceAvailable' | translateWF: 'Available'" />
                        } @else {
                          <p-tag severity="secondary" [value]="'PoSettlement_AdvanceDrawnDown' | translateWF: 'Fully applied'" />
                        }
                      </div>
                      <span class="doc-card__total tabular-nums">{{ adv.allocated | number: '1.2-2' }}</span>
                    </div>
                    <div class="doc-card__meta">{{ adv.date | date: 'dd/MM/yyyy' }} · {{ adv.actor }}</div>
                    <div class="doc-card__amounts">
                      <div>
                        <span class="doc-card__amount-label">{{ 'PoSettlement_AdvanceAllocatedShort' | translateWF: 'Advanced' }}</span>
                        <span class="tabular-nums">{{ adv.allocated | number: '1.2-2' }}</span>
                      </div>
                      <div>
                        <span class="doc-card__amount-label">{{ 'PoSettlement_AdvanceAppliedShort' | translateWF: 'Applied' }}</span>
                        <span class="tabular-nums doc-card__paid">{{ adv.applied | number: '1.2-2' }}</span>
                      </div>
                      <div>
                        <span class="doc-card__amount-label">{{ 'PoSettlement_AdvanceRemaining' | translateWF: 'Available' }}</span>
                        <span class="tabular-nums">{{ adv.remaining | number: '1.2-2' }}</span>
                      </div>
                    </div>
                  </div>
                }
              </p-card>
            </p-panel>
          }

          <!-- Goods receipts (#5235). The goods side of the settlement: what actually arrived against the
               PO, so a clerk can see that an invoice matches a delivery without leaving the page. -->
          @if (d.goodsReceipts.length) {
            <p-panel class="mb-[1.5rem]" [header]="'PoSettlement_GoodsReceipts' | translateWF: 'Goods received'">
              <p-card>
                @for (grn of d.goodsReceipts; track grn.id) {
                  <div class="doc-card mb-[0.75rem]">
                    <div class="doc-card__head">
                      <div class="doc-card__identity">
                        <span>{{ grn.number }}</span>
                        @if (grn.isCompleted) {
                          <p-tag severity="success" [value]="'PoSettlement_GrnReceived' | translateWF: 'Received'" />
                        } @else {
                          <p-tag severity="warn" [value]="'PoSettlement_GrnInProgress' | translateWF: 'In progress'" />
                        }
                      </div>
                      <span class="doc-card__total tabular-nums">{{ grn.totalQuantity | number: '1.0-2' }}</span>
                    </div>
                    <div class="doc-card__meta">
                      @if (grn.receivedDate) {
                        <!-- 'UTC' is load-bearing: the received date is a pure calendar date stamped at
                           midnight, and converting it to a UTC-6 browser shows the day before (#5234). -->
                        {{ grn.receivedDate | date: 'dd/MM/yyyy' : 'UTC' }}
                      } @else {
                        {{ 'PoSettlement_GrnNotReceivedYet' | translateWF: 'Not yet received' }}
                      }
                      @if (grn.locationCode) {
                        · {{ grn.locationCode }}
                      }
                      @if (grn.receiverUsername) {
                        · {{ grn.receiverUsername }}
                      }
                    </div>
                    @if (grn.lines.length) {
                      <!-- Tailwind utilities rather than component SCSS: this file is already at the
                         4.10 kB budget ceiling, and new rules here tip the production build. -->
                      <table class="w-full table-fixed border-collapse text-[0.8125rem]">
                        <thead>
                          <tr class="text-[var(--p-text-muted-color)]">
                            <th class="border-b border-[var(--p-content-border-color)] py-1 text-left font-medium">
                              {{ 'PoSettlement_GrnItem' | translateWF: 'Item' }}
                            </th>
                            <th class="w-24 border-b border-[var(--p-content-border-color)] py-1 text-right font-medium">
                              {{ 'PoSettlement_GrnQuantity' | translateWF: 'Quantity' }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (line of grn.lines; track line.itemCode) {
                            <tr>
                              <td class="min-w-0 py-1 align-top">
                                <!-- The code always shows: it identifies the item on its own, so a name
                                   that failed to resolve costs detail rather than identity. -->
                                <span class="mr-1.5 text-[var(--p-text-muted-color)]">({{ line.itemCode }})</span>
                                @let itemName = itemNameFor(line.itemCode, line.itemDisplayName);
                                @if (itemName) {
                                  <!-- The name gives way first when the column is tight; the code stays whole. -->
                                  <span class="inline-block max-w-full truncate align-bottom" [title]="itemName">{{ itemName }}</span>
                                }
                              </td>
                              <td class="py-1 text-right align-top tabular-nums">{{ line.quantity | number: '1.0-2' }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    }
                  </div>
                }
              </p-card>
            </p-panel>
          }

          <p-panel [header]="'PoSettlement_Activity' | translateWF: 'Activity'">
            <p-card>
              @if (!d.activity.length) {
                <div class="po-detail__empty">{{ 'PoSettlement_NoActivity' | translateWF: 'No activity yet' }}</div>
              }

              <div class="activity">
                @for (entry of d.activity; track $index) {
                  <div class="activity__item">
                    <span class="activity__icon" [class.activity__icon--posted]="entry.kind === ActivityKind.PaymentPosted || entry.kind === ActivityKind.AdvancePosted">
                      <i [class]="activityIcon(entry.kind)"></i>
                    </span>
                    <div class="activity__body">
                      <div class="activity__head">
                        <span class="activity__title">{{ activityLabel(entry.kind) }}</span>
                        <span class="tabular-nums activity__amount">{{ entry.amount | number: '1.2-2' }}</span>
                      </div>
                      <div class="activity__sub">{{ entry.referenceNumber }} · {{ entry.documentNumber }}</div>
                      <div class="activity__sub">{{ entry.date | date: 'dd/MM/yyyy' }} · {{ entry.actor }}</div>
                    </div>
                  </div>
                }
              </div>
            </p-card>
          </p-panel>
        </div>
      </div>
    }
  </div>
</main>
